import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import HistoryIcon from "@mui/icons-material/History";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import type { DispatchAction } from "@storyboard/dispatch-view/view";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import { type DispatchTableRow, rowFeedId } from "@/hooks/dispatch/dispatchTableRow";
import { type ComposerHandle, dispatchDraftKey, useComposerState } from "@/hooks/dispatch/useDispatchComposer";
import { zinc } from "@/ui/tokens";
import DispatchComposer, { DISPATCH_COMPOSER_INPUT_ID } from "./DispatchComposer";
import { DISPATCH_HIGHLIGHT } from "./dispatchColors";

/**
 * The detail's height in px with an empty reply: the virtualizer's estimate until the rendered detail is measured.
 * The detail sizes to its content, so a reply that wraps onto more lines grows it, and the table measures it again.
 */
export const DISPATCH_DETAIL_ESTIMATED_HEIGHT = 44;

/**
 * An action's tooltip with its key, or the bare label for an action a keymap collision left unbound.
 */
function withKey(label: string, binding: string): string {
  return binding ? `${label} (${binding})` : label;
}

/**
 * What the selected row unfolds into: one line holding the inline reply, whose placeholder names its key, and the
 * History / Context / Acknowledge / Acknowledge channel actions. The row's own cells carry the message, so nothing the
 * row already says repeats here. It sizes to the reply, growing only while a typed reply wraps, and the table holds it
 * to the width on screen. The row and the detail read as one block: the same tint, one bar down their left edge (the
 * row's first cell draws its half), and no divider between them. The block turns purple while the reply has the
 * cursor.
 */
export default function DispatchRowDetail({
  row,
  reply,
  keymap,
  onOpenHistory,
  onOpenContext,
  onAcknowledge,
  onAcknowledgeChannel,
}: {
  row: DispatchTableRow;
  /** The page's inline reply (`useDispatchComposer`); the draft is read below the detail, by the reply alone. */
  reply: ComposerHandle;
  keymap: Record<DispatchAction, string>;
  onOpenHistory: () => void;
  onOpenContext: () => void;
  /** Acknowledges what the row is about at the view's grain: the driver's channel, or the one message. */
  onAcknowledge: () => void;
  /** Acknowledges the driver's whole channel. */
  onAcknowledgeChannel: () => void;
}) {
  // Translucent, so the buttons sit on the resting tint and on the purple while the reply has the cursor.
  const actionSx = {
    color: zinc[300],
    bgcolor: "rgba(255, 255, 255, 0.08)",
    borderRadius: 1,
    width: 30,
    height: 30,
    flex: "none",
    "&:hover": { color: "white", bgcolor: "rgba(255, 255, 255, 0.16)" },
  };
  const { selected, composing } = DISPATCH_HIGHLIGHT;

  return (
    <Box
      data-sb={TestIds.dispatch.rowDetail}
      onClick={(event) => event.stopPropagation()}
      sx={{
        boxSizing: "border-box",
        px: 2,
        py: 0.5,
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        bgcolor: selected.tint,
        boxShadow: `inset 3px 0 0 ${selected.bar}`,
        borderBottom: `1px solid ${zinc[700]}`,
        // Purple while the reply has the cursor, as the row above turns (`DispatchTable`); teal again once it leaves.
        "&:has(textarea:focus)": { bgcolor: composing.tint, boxShadow: `inset 3px 0 0 ${composing.bar}` },
      }}
    >
      <RowReply reply={reply} row={row} placeholder={withKey("Reply", keymap.reply)} />
      {/* As tall as the reply's one-line box, so the actions center on its first line and stay there as it grows. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "none", minHeight: 36 }}>
        <Tooltip title={withKey("History", keymap.openHistory)}>
          <IconButton size="small" aria-label="History" onClick={onOpenHistory} sx={actionSx}>
            <HistoryIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={withKey("Context", keymap.openContext)}>
          <IconButton size="small" aria-label="Context" onClick={onOpenContext} sx={actionSx}>
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={withKey("Acknowledge", keymap.acknowledge)}>
          <IconButton size="small" aria-label="Acknowledge" onClick={onAcknowledge} sx={actionSx}>
            <DoneIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={withKey("Acknowledge channel", keymap.acknowledgeChannel)}>
          <IconButton size="small" aria-label="Acknowledge channel" onClick={onAcknowledgeChannel} sx={actionSx}>
            <DoneAllIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

/**
 * The inline reply for `row`: the one part of the detail that reads the draft, so a keystroke re-renders it alone.
 */
function RowReply({ reply, row, placeholder }: { reply: ComposerHandle; row: DispatchTableRow; placeholder: string }) {
  const composer = useComposerState(reply, dispatchDraftKey(row));
  return (
    <DispatchComposer
      composer={composer}
      feedId={rowFeedId(row)}
      variant="row"
      inputId={DISPATCH_COMPOSER_INPUT_ID}
      placeholder={placeholder}
      testId={TestIds.dispatch.composer}
      sendTestId={TestIds.dispatch.composerSend}
    />
  );
}
